import React, { useState, useEffect, useCallback } from "react";
import {
  ScrollView,
  FlatList,
  View,
  Text,
  TouchableOpacity,
  ActivityIndicator,
  Image,
  KeyboardAvoidingView,
  Platform,
} from "react-native";
import { Toast } from "react-native-toast-notifications";
import customAxios from "../../../utils/customAxios";
import i18n from "../../../locales/i18n";
import StandardNavigation from "../../../components/navigation/StandardNavigation";
import QuantityInput from "../../../components/scanner/QuantityInput";
import ProductSearch from "../../../components/scanner/ProductSearch";
import BillModal from "../../../components/scanner/BillModal";
import { router, useFocusEffect } from "expo-router";
import AntDesign from "@expo/vector-icons/AntDesign";
import { useDispatch, useSelector } from "react-redux";
import { appActions } from "../../../store/slices/appSlice";
import { RootState } from "../../../store";
import SliderShow from "../../../components/scanner/SliderShow";
import { useCart } from "../../../hooks/useCart";
import Animated, { FadeInDown } from "react-native-reanimated";
import OtherProductInput from "../../../components/scanner/OtherProductInput";
import { useStock } from "../../../hooks/useStock";
import WebSellScreen from "../../../components/web/WebSellScreen";

interface Product {
  id_product: number;
  name: string;
  price_sell: string;
  price_bought: string;
  stock: number;
  photo?: string;
  quantity?: number;
  barcode?: string | number;
}

export default function Scanner() {
  const dispatch = useDispatch();
  const { sellWithScanner, locale } = useSelector(
    (state: RootState) => state.app,
  );

  // Use the custom stock hook
  const {
    products: allStockProducts,
    isLoading: stockLoading,
    fetchProducts,
    searchProducts,
  } = useStock();

  // Use Redux-based cart for persistent cart data
  const {
    selectedProducts,
    quantities,
    totalPrice,
    boughtPrice,
    addProduct,
    updateQuantity,
    removeProduct,
    calculateTotals,
    clearCart,
    setSelectedProducts,
  } = useCart();

  const [isChecked, setChecked] = useState(false);
  const [buttonSpinner, setButtonSpinner] = useState(false);
  const [products, setProducts] = useState<Product[]>([]);
  const [selectedTag, setSelectedTag] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);
  const [OpenModal, setOpenModel] = useState(false);
  const [ShowmoRe, SetShowMore] = useState(true);
  const [buttonSpinner2, setButtonSpinner2] = useState(false);
  const [time, SetTimeResponse] = useState("");
  const [date, SetDateResponse] = useState("");
  const [sound, setSound] = useState<any>();
  const [autherItem, setAutherItem] = useState("");
  const [billData, setBillData] = useState<any>({});
  const [OpenCam, SetOpenCam] = useState(true);
  const [totalOther, setTotalOther] = useState(0);
  const [allProducts, setAllProducts] = useState<any[]>([]);
  const [custumQunatity, setCustumQuantity] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const [factureId, setFactureId] = useState<number | null>(null);
  const [soldTotal, setSoldTotal] = useState<number>(0);

  let allproducts: any[] = [];

  // Update locale when screen is focused
  useFocusEffect(
    useCallback(() => {
      dispatch(appActions.setLocale(i18n.locale));
    }, [dispatch]),
  );

  // Fetch stock products when screen is focused
  useFocusEffect(
    useCallback(() => {
      fetchProducts();
    }, [fetchProducts]),
  );

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (searchQuery && searchQuery.trim()) {
        handelSearchProduct(searchQuery);
      } else {
        // Clear products when search is empty
        setProducts([]);
        setIsDropdownVisible(false);
      }
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchQuery]);

  useEffect(() => {
    calculateTotals();
  }, [quantities, selectedProducts, calculateTotals]);

  useEffect(() => {
    return sound
      ? () => {
          sound.unloadAsync();
        }
      : undefined;
  }, [sound]);

  const handelSearchProduct = async (productName: string) => {
    if (!productName || productName.trim() === "") {
      setProducts([]);
      setButtonSpinner(false);
      return;
    }

    setButtonSpinner(true);

    try {
      // Search locally using Redux store
      let localResults: Product[] = [];

      try {
        // Search all products (with or without barcode)
        localResults = allStockProducts.filter((product: any) =>
          product.name.toLowerCase().includes(productName.toLowerCase()),
        );
      } catch (searchError) {
        localResults = [];
      }

      if (localResults.length > 0) {
        setProducts(localResults as Product[]);
        setButtonSpinner(false);
        setIsDropdownVisible(true); // Make sure dropdown is visible
        return;
      }

      setButtonSpinner(false);
    } catch (error) {
      setButtonSpinner(false);
      setProducts([]);
      setIsDropdownVisible(false);
      Toast.show(i18n.t("searchProductsError"), {
        type: "danger",
      });
    }
  };

  const handelAddProduct = () => {
    // Handle custom quantity for selected product
    if (custumQunatity && selectedTag) {
      const quantity = parseFloat(custumQunatity);
      if (quantity > 0) {
        updateQuantity(selectedTag, quantity);
        setCustumQuantity("");
        Toast.show(i18n.t("quantityUpdated"), {
          type: "success",
        });
        return;
      } else {
        Toast.show(i18n.t("enterValidQuantity"), {
          type: "danger",
        });
        return;
      }
    }

    // Handle adding "other" item with custom price
    if (autherItem && autherItem.match(/^-?\d*\.?\d+$/)) {
      const uniqueId = Date.now();
      const quantity = custumQunatity ? parseFloat(custumQunatity) : 1;

      if (custumQunatity && quantity <= 0) {
        Toast.show(i18n.t("enterValidQuantity"), {
          type: "danger",
        });
        return;
      }

      allproducts.push({
        id_product: 0,
        quantity: quantity,
        product_name: "other",
      });

      const otherProduct = {
        id_product: uniqueId,
        quantity: quantity,
        name: "other",
        price_bought: "0",
        price_sell: autherItem,
        stock: 1,
      };

      addProduct(otherProduct, quantity);

      Toast.show(i18n.t("productAddedSuccess"), {
        type: "success",
      });
      setTotalOther((prev) => prev + +autherItem * quantity);
      setAutherItem("");
      setCustumQuantity("");
      return;
    }

    if (autherItem && !autherItem.match(/^[0-9]+$/)) {
      Toast.show(i18n.t("enterValidValue"), {
        type: "danger",
      });
    }
  };

  const removeTag = (index: number) => {
    const productToRemove = selectedProducts[index];
    if (productToRemove) {
      removeProduct(productToRemove.id_product);
      if (selectedTag === productToRemove.id_product) {
        setSelectedTag(null);
      }
    }
  };

  const handleSelect = (item: Product) => {
    // Check if product already exists in the selected list
    const existingProductIndex = selectedProducts.findIndex(
      (prod: any) => prod.id_product === item.id_product,
    );

    if (existingProductIndex === -1) {
      // Product doesn't exist, add it
      setSearchQuery(item.name);
      const quantity = custumQunatity ? parseFloat(custumQunatity) : 1;
      addProduct(item, quantity > 0 ? quantity : 1);
      setIsDropdownVisible(false);
      setSearchQuery("");
      setCustumQuantity("");

      Toast.show(`${i18n.t("productAdded")} ${item.name}`, {
        type: "success",
      });
    } else {
      // Product exists, increase quantity
      const quantity = custumQunatity ? parseFloat(custumQunatity) : 1;
      const currentQuantity = quantities[item.id_product] || 1;
      updateQuantity(
        item.id_product,
        currentQuantity + (quantity > 0 ? quantity : 1),
      );
      setSearchQuery("");
      setIsDropdownVisible(false);
      setCustumQuantity("");

      Toast.show(`${i18n.t("quantityUpdated")} ${item.name}`, {
        type: "success",
      });
    }

    setSelectedTag(item.id_product);
  };

  const handleIncrementQuantity = () => {
    if (selectedTag) {
      const currentQuantity = quantities[selectedTag] || 1;
      updateQuantity(selectedTag, currentQuantity + 1);
    }
  };

  const handleDecrementQuantity = () => {
    if (selectedTag) {
      const currentQuantity = quantities[selectedTag] || 1;
      if (currentQuantity > 1) {
        updateQuantity(selectedTag, currentQuantity - 1);
      }
    }
  };

  const handelConfirm = async () => {
    if (selectedProducts.length === 0) {
      Toast.show(i18n.t("searchForProduct"), {
        type: "danger",
      });
      return;
    }
    if (totalPrice < 0) {
      Toast.show(i18n.t("totalCannotBeNegative"), {
        type: "danger",
      });
      return;
    }

    const newProducts = selectedProducts.map((item: Product) => {
      if (item.name === "other") {
        return {
          id_product: 0,
          quantity: quantities[item.id_product] || 1,
          product_name: "other",
        };
      } else {
        return {
          id_product: item.id_product,
          quantity: quantities[item.id_product] || 1,
          product_name: item.name,
        };
      }
    });
    setAllProducts(newProducts);

    selectedProducts.forEach((item: Product) => {
      if (item.name === "other") {
        allproducts.push({
          id_product: 0,
          price_sell: item.price_sell,
          quantity: quantities[item.id_product] || 1,
          product_name: "other",
        });
      } else {
        allproducts.push({
          id_product: item.id_product,
          price_sell: item.price_sell,
          quantity: quantities[item.id_product] || 1,
          product_name: item.name,
        });
      }
    });

    setAutherItem("");
    setButtonSpinner2(true);

    const profit = selectedProducts.reduce((acc: number, item: Product) => {
      if (item.name !== "other") {
        return (
          acc +
          (quantities[item.id_product] || 1) *
            (parseFloat(item.price_sell) - parseFloat(item.price_bought))
        );
      }
      return acc;
    }, 0);

    await customAxios
      .post("/sell/client/checkout", {
        total: totalPrice,
        profit: profit,
        capital: boughtPrice,
        listofproducts: allproducts,
      })
      .then(async (res) => {
        Toast.show(i18n.t("saleSuccess"), { type: "success" });
        setOpenModel(true);
        setButtonSpinner2(false);
        setFactureId(res.data.id_facture);

        // Trigger stats refresh
        dispatch(appActions.toggleStatsRefresh());

        // Store the total before clearing cart (so modal can display it)
        setSoldTotal(totalPrice);

        // Clear cart after successful sale
        clearCart();

        // Reset form state
        setAutherItem("");
        setCustumQuantity("");
        setSelectedTag(null);
        setSearchQuery("");
        setIsDropdownVisible(false);
        setProducts([]);
        setTotalOther(0);
        setAllProducts([]);
        setSearchValue("");
        allproducts = [];

        SetOpenCam(false);
        setBillData(res.data);
        if (res.data?.created_at) {
          let dateObj = new Date(res.data.created_at);
          if (!isNaN(dateObj.getTime())) {
            let formattedDate = dateObj.toISOString().split("T")[0];
            let formattedTime = dateObj
              .toISOString()
              .split("T")[1]
              .split(".")[0];
            SetDateResponse(formattedDate);
            SetTimeResponse(formattedTime);
          } else {
            // Invalid date
          }
        } else {
          // created_at is undefined or invalid in response data
        }
      })
      .catch((error) => {
        setButtonSpinner2(false);

        // Check if this is a free tier limit exceeded error (403)
        if (error?.response?.status === 403) {
          const errorMessage = error?.response?.data?.error || "";
          if (
            typeof errorMessage === "string" &&
            errorMessage.toLowerCase().includes("free tier")
          ) {
            // Show upgrade message and navigate to limits page
            Toast.show(
              i18n.t("freeTierLimitExceeded") + " " + i18n.t("upgradeRequired"),
              {
                type: "danger",
                duration: 5000,
              },
            );

            // Navigate to limits page after a short delay
            setTimeout(() => {
              router.push("/(drawer)/params/limits");
            }, 2000);
            return;
          }
        }

        // Show more specific error message for other errors
        const errorMessage =
          error.response?.data?.message ||
          error.message ||
          i18n.t("billNotSaved");
        Toast.show(errorMessage, { type: "danger" });
      });
  };

  // Render web version on web platform
  if (Platform.OS === "web") {
    return <WebSellScreen />;
  }

  return (
    <View style={{ flex: 1, backgroundColor: "#FFFFFF" }}>
      {/* Standard Navigation Header */}
      <StandardNavigation
        showBackButton={true}
        showMenuButton={true}
        showLogo={true}
        shareRoute
      />
      <KeyboardAvoidingView
        behavior={Platform.OS === "ios" ? "padding" : "height"}
        style={{ flex: 1 }}
        keyboardVerticalOffset={Platform.OS === "ios" ? 0 : 20}
      >
        <ScrollView
          style={{ flex: 1 }}
          showsVerticalScrollIndicator={false}
          keyboardShouldPersistTaps="handled"
        >
          {/* Total Section with White Background */}
          <Animated.View
            entering={FadeInDown.duration(600).delay(100)}
            style={{
              backgroundColor: "white",
              paddingHorizontal: 20,
              paddingVertical: 15,
              marginHorizontal: 15,
              marginTop: 20, // Added more spacing from header
              borderRadius: 10,
              shadowColor: "#000",
              shadowOffset: { width: 0, height: 2 },
              shadowOpacity: 0.1,
              shadowRadius: 4,
              elevation: 3,
            }}
          >
            <View
              style={{
                flexDirection: "row-reverse",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Text
                style={{
                  fontSize: 18,
                  fontWeight: "bold",
                  color: "#212121",
                }}
              >
                {i18n.t("totalColon")}
              </Text>
              <Text
                style={{
                  fontSize: 18,
                  fontWeight: "bold",
                  color: "#212121",
                }}
              >
                {totalPrice} {i18n.t("currency")}
              </Text>
            </View>
          </Animated.View>

          {/* Search Input - Using ProductSearch Component */}
          <Animated.View
            entering={FadeInDown.duration(600).delay(200)}
            style={{
              paddingHorizontal: 20,
              marginTop: 15,
              marginHorizontal: "auto",
            }}
          >
            <ProductSearch
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              setIsDropdownVisible={setIsDropdownVisible}
              SetOpenCam={() => {}} // No camera in this page
              locale={locale}
            />
          </Animated.View>

          {/* Loading Spinner */}
          {buttonSpinner && (
            <View style={{ paddingVertical: 20, alignItems: "center" }}>
              <ActivityIndicator size="large" color="#1E88E5" />
            </View>
          )}

          {/* Search Results Dropdown */}
          {isDropdownVisible && (
            <View
              style={{
                paddingHorizontal: 20,
                marginTop: 10,
                maxHeight: 200,
              }}
            >
              {products.length > 0 ? (
                <FlatList
                  data={products}
                  keyExtractor={(item) => item.id_product.toString()}
                  showsVerticalScrollIndicator={true}
                  nestedScrollEnabled={true}
                  scrollEnabled={true}
                  renderItem={({ item }) => (
                    <TouchableOpacity
                      style={{
                        backgroundColor: "#1E88E5",
                        borderRadius: 10,
                        marginBottom: 8,
                        padding: 12,
                        flexDirection: "row",
                        alignItems: "center",
                      }}
                      onPress={() => handleSelect(item)}
                    >
                      {item.photo ? (
                        <Image
                          source={{ uri: item.photo }}
                          style={{
                            width: 40,
                            height: 40,
                            borderRadius: 20,
                            marginRight: 12,
                          }}
                          resizeMode="cover"
                        />
                      ) : (
                        <View
                          style={{
                            width: 40,
                            height: 40,
                            borderRadius: 20,
                            marginRight: 12,
                            backgroundColor: "rgba(255,255,255,0.2)",
                            justifyContent: "center",
                            alignItems: "center",
                          }}
                        >
                          <Text style={{ color: "white", fontSize: 10 }}>
                            No Image
                          </Text>
                        </View>
                      )}
                      <View style={{ flex: 1 }}>
                        <Text
                          style={{
                            color: "white",
                            fontSize: 16,
                            fontWeight: "600",
                            marginBottom: 2,
                          }}
                        >
                          {item.name}
                        </Text>
                        <Text style={{ color: "white", fontSize: 12 }}>
                          {i18n.t("price")}: {item.price_sell}{" "}
                          {i18n.t("currency")}
                        </Text>
                      </View>
                    </TouchableOpacity>
                  )}
                />
              ) : (
                <View
                  style={{
                    backgroundColor: "#F5F5F5",
                    borderRadius: 10,
                    padding: 15,
                    alignItems: "center",
                  }}
                >
                  <Text style={{ color: "#4e4c4cff", fontSize: 16 }}>
                    {i18n.t("noproducts")}
                  </Text>
                </View>
              )}
            </View>
          )}

          {/* Product Grid/Slider */}
          <Animated.View
            entering={FadeInDown.duration(600).delay(300)}
            style={{
              paddingHorizontal: 20,
              marginTop: 20,
            }}
          >
            {/* SliderShow for product recommendations */}
            <SliderShow
              onProductSelect={handleSelect}
              custumQunatity={custumQunatity}
            />
          </Animated.View>

          {/* Add Button and Quantity Section */}
          <Animated.View
            entering={FadeInDown.duration(600).delay(400)}
            style={{
              paddingHorizontal: 20,
              marginBottom: 20,
            }}
          >
            <View
              className={`${
                locale == "fr" ? "flex-row-reverse" : "flex-row"
              }   flex items-center justify-center`}
              style={{ maxWidth: 600 }}
            >
              {/* Quantity Control */}
              <QuantityInput
                custumQunatity={custumQunatity}
                setCustumQuantity={setCustumQuantity}
                selectedTag={selectedTag}
                handleIncrementQuantity={handleIncrementQuantity}
                handleDecrementQuantity={handleDecrementQuantity}
                handelAddProduct={handelAddProduct}
              />
              <OtherProductInput
                autherItem={autherItem}
                setAutherItem={setAutherItem}
                custumQunatity={custumQunatity}
                handelAddProduct={handelAddProduct}
                locale={locale}
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                isDropdownVisible={isDropdownVisible}
                setIsDropdownVisible={setIsDropdownVisible}
                products={products}
                buttonSpinner={buttonSpinner}
                handleSelect={handleSelect}
                SetOpenCam={SetOpenCam}
              />
            </View>
          </Animated.View>

          {/* Product Tags Section - Horizontal Slider */}
          <Animated.View
            entering={FadeInDown.duration(600).delay(500)}
            style={{ paddingHorizontal: 20, marginBottom: 20 }}
          >
            <ScrollView
              horizontal
              showsHorizontalScrollIndicator={false}
              contentContainerStyle={{
                paddingHorizontal: 5,
                gap: 10,
              }}
              style={{
                flexDirection: "row",
              }}
            >
              {selectedProducts.map((item, index) => (
                <TouchableOpacity
                  key={item.id_product}
                  onPress={() => setSelectedTag(item.id_product)}
                  style={{
                    flexDirection: "row",
                    alignItems: "center",
                    backgroundColor:
                      selectedTag === item.id_product ? "#E3F2FD" : "#F5F5F5",
                    borderRadius: 25,
                    paddingHorizontal: 15,
                    paddingVertical: 10,
                    borderWidth: 2,
                    borderColor:
                      selectedTag === item.id_product ? "#1976D2" : "#E0E0E0",
                    minWidth: 80,
                  }}
                >
                  <TouchableOpacity
                    onPress={() => removeTag(index)}
                    style={{
                      backgroundColor: "#F44336",
                      borderRadius: 10,
                      width: 18,
                      height: 18,
                      justifyContent: "center",
                      alignItems: "center",
                      marginRight: 8,
                    }}
                  >
                    <Text
                      style={{
                        color: "white",
                        fontSize: 10,
                        fontWeight: "bold",
                      }}
                    >
                      ×
                    </Text>
                  </TouchableOpacity>
                  <Text
                    style={{
                      fontSize: 14,
                      color:
                        selectedTag === item.id_product ? "#1976D2" : "#757575",
                      fontWeight: "600",
                      marginRight: 8,
                    }}
                  >
                    {item.name == "other" ? `${item.price_sell}` : item.name}
                  </Text>
                  <View
                    style={{
                      backgroundColor: "#4CAF50",
                      borderRadius: 12,
                      width: 22,
                      height: 22,
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <Text
                      style={{
                        color: "white",
                        fontSize: 12,
                        fontWeight: "bold",
                      }}
                    >
                      {quantities[item.id_product] || 1}
                    </Text>
                  </View>
                </TouchableOpacity>
              ))}
            </ScrollView>
          </Animated.View>

          {/* Sell Without Scan Button */}

          <Animated.View entering={FadeInDown.duration(600).delay(500)}>
            <TouchableOpacity
              onPress={() =>
                sellWithScanner
                  ? router.back()
                  : router.push("/(drawer)/scanner")
              }
              className={`${
                locale == "fr"
                  ? "flex-row items-end"
                  : "flex-row-reverse items-start"
              }
          flex items-center  px-4 my-2 
          `}
            >
              <View className="border border-primaryV2 rounded-xl  p-2">
                <AntDesign name="double-right" size={24} color="#279F9E" />
              </View>

              <Text
                className={`${
                  locale == "ar" ? "text-right" : "text-left"
                } text-lg font-semibold px-4`}
                style={{ color: "#279F9E" }}
              >
                {i18n.t("sellWith")}
              </Text>
            </TouchableOpacity>
          </Animated.View>

          {/* Main Confirm Button */}
          <Animated.View
            entering={FadeInDown.duration(600).delay(700)}
            style={{ paddingHorizontal: 20, marginBottom: 30 }}
          >
            <TouchableOpacity
              onPress={handelConfirm}
              disabled={buttonSpinner2}
              style={{
                backgroundColor: "#1976D2",
                borderRadius: 25,
                paddingVertical: 15,
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              {buttonSpinner2 ? (
                <ActivityIndicator size="small" color="white" />
              ) : (
                <Text
                  style={{
                    color: "white",
                    fontSize: 18,
                    fontWeight: "600",
                  }}
                >
                  {i18n.t("confirm")}
                </Text>
              )}
            </TouchableOpacity>
          </Animated.View>
        </ScrollView>

        {/* Modals */}
        <BillModal
          OpenModal={OpenModal}
          factureId={factureId}
          setOpenModel={setOpenModel}
          billData={billData}
          totalPrice={soldTotal}
          time={time}
          date={date}
          ShowmoRe={ShowmoRe}
          SetShowMore={SetShowMore}
          allProducts={allProducts}
          buttonSpinner={buttonSpinner}
          SetOpenCam={SetOpenCam}
          setTotalOther={setTotalOther}
          selectedProduct={selectedProducts}
          setSelectedProducts={setSelectedProducts}
          setProducts={setProducts}
          setAllProducts={setAllProducts}
          locale={locale}
          isChecked={isChecked}
          setChecked={setChecked}
          clearCart={clearCart}
        />
      </KeyboardAvoidingView>
    </View>
  );
}
